export type ZipProgressSample = {
	at?: number
	filesProcessed: number
	totalFiles: number
}

export type ZipProgressPredictor = {
	beginPreparing: (totalFiles: number, at?: number) => void
	beginZipping: (at?: number) => void
	getPercentComplete: (now?: number) => number
	recordProgress: (sample: ZipProgressSample) => void
	reset: () => void
	setTotalFiles: (totalFiles: number) => void
}

/** Zip phase occupies this share of the full progress bar. */
export const ZIP_PROGRESS_BAR_MAX = 50

/** Steady floor while preparing / until estimates rise above it (full bar %). */
export const ZIP_PROGRESS_FLOOR_PERCENT = 3

/** Preparing creep: 0 → floor. */
export const ZIP_PREPARING_CREEP_MS = 500

/** First-track crawl: floor → first-file share of the zip half. */
export const ZIP_FIRST_FILE_CRAWL_MS = 3_000

type ZipProgressPhase = 'idle' | 'preparing' | 'firstFileCrawl' | 'estimated'

export const createZipProgressPredictor = (): ZipProgressPredictor => {
	let estimatedTotalMs: number | null = null
	let filesProcessed = 0
	let firstFileCrawlFromPercent = ZIP_PROGRESS_FLOOR_PERCENT
	let firstFileCrawlStartedAt: number | null = null
	let highestPercentComplete = 0
	let phase: ZipProgressPhase = 'idle'
	let preparingStartedAt: number | null = null
	let totalFiles = 0
	let zippingStartedAt: number | null = null

	const getFirstFileSharePercent = (): number => {
		if (totalFiles <= 0) {
			return ZIP_PROGRESS_FLOOR_PERCENT
		}

		return ZIP_PROGRESS_BAR_MAX / totalFiles
	}

	const getFirstFileCrawlTargetPercent = (): number =>
		Math.max(ZIP_PROGRESS_FLOOR_PERCENT, getFirstFileSharePercent())

	const getPreparingPercent = (now: number): number => {
		if (preparingStartedAt === null) {
			return 0
		}

		const elapsedMs = Math.max(0, now - preparingStartedAt)

		return Math.min(
			ZIP_PROGRESS_FLOOR_PERCENT,
			(elapsedMs / ZIP_PREPARING_CREEP_MS) * ZIP_PROGRESS_FLOOR_PERCENT
		)
	}

	const getFirstFileCrawlPercent = (now: number): number => {
		if (firstFileCrawlStartedAt === null) {
			return firstFileCrawlFromPercent
		}

		const targetPercent = getFirstFileCrawlTargetPercent()
		const crawlSpan = targetPercent - firstFileCrawlFromPercent

		if (crawlSpan <= 0) {
			return firstFileCrawlFromPercent
		}

		const elapsedMs = Math.max(0, now - firstFileCrawlStartedAt)
		const crawlProgress = Math.min(1, elapsedMs / ZIP_FIRST_FILE_CRAWL_MS)

		return firstFileCrawlFromPercent + crawlSpan * crawlProgress
	}

	const getEstimatedPercent = (now: number): number => {
		if (zippingStartedAt === null || totalFiles <= 0) {
			return ZIP_PROGRESS_FLOOR_PERCENT
		}

		if (filesProcessed > 0 && filesProcessed >= totalFiles) {
			return ZIP_PROGRESS_BAR_MAX
		}

		const fileFloorPercent =
			(filesProcessed / totalFiles) * ZIP_PROGRESS_BAR_MAX
		const elapsedSinceZippingStarted = Math.max(0, now - zippingStartedAt)

		if (estimatedTotalMs === null || estimatedTotalMs <= 0) {
			return Math.max(ZIP_PROGRESS_FLOOR_PERCENT, fileFloorPercent)
		}

		const timePercent = Math.min(
			ZIP_PROGRESS_BAR_MAX,
			(elapsedSinceZippingStarted / estimatedTotalMs) * ZIP_PROGRESS_BAR_MAX
		)

		return Math.min(
			ZIP_PROGRESS_BAR_MAX,
			Math.max(ZIP_PROGRESS_FLOOR_PERCENT, fileFloorPercent, timePercent)
		)
	}

	const getRawPercentComplete = (now: number): number => {
		if (phase === 'idle') {
			return 0
		}

		if (phase === 'preparing') {
			return getPreparingPercent(now)
		}

		if (phase === 'firstFileCrawl') {
			return getFirstFileCrawlPercent(now)
		}

		return getEstimatedPercent(now)
	}

	const enterEstimatedPhase = (at: number): void => {
		phase = 'estimated'

		if (zippingStartedAt === null) {
			zippingStartedAt = at
		}

		if (filesProcessed <= 0 || totalFiles <= 0) {
			estimatedTotalMs = null
			return
		}

		const elapsedMs = at - zippingStartedAt

		if (elapsedMs <= 0) {
			estimatedTotalMs = null
			return
		}

		estimatedTotalMs = elapsedMs * (totalFiles / filesProcessed)
	}

	return {
		beginPreparing: (nextTotalFiles, at = Date.now()): void => {
			totalFiles = nextTotalFiles
			preparingStartedAt = at
			phase = 'preparing'
			filesProcessed = 0
			estimatedTotalMs = null
			firstFileCrawlStartedAt = null
			zippingStartedAt = null
			highestPercentComplete = 0
		},
		beginZipping: (at = Date.now()): void => {
			if (phase === 'firstFileCrawl' || phase === 'estimated') {
				return
			}

			const currentPercent = getRawPercentComplete(at)

			zippingStartedAt = at
			firstFileCrawlStartedAt = at
			firstFileCrawlFromPercent = Math.max(
				currentPercent,
				ZIP_PROGRESS_FLOOR_PERCENT
			)
			phase = 'firstFileCrawl'
		},
		getPercentComplete: (now = Date.now()): number => {
			highestPercentComplete = Math.max(
				highestPercentComplete,
				getRawPercentComplete(now)
			)

			return highestPercentComplete
		},
		recordProgress: ({
			at = Date.now(),
			filesProcessed: nextFilesProcessed,
			totalFiles: nextTotalFiles,
		}): void => {
			filesProcessed = nextFilesProcessed
			totalFiles = nextTotalFiles

			if (nextFilesProcessed >= 2) {
				enterEstimatedPhase(at)
				return
			}

			if (phase === 'idle' || phase === 'preparing') {
				const currentPercent = getRawPercentComplete(at)

				zippingStartedAt = zippingStartedAt ?? at
				firstFileCrawlStartedAt = at
				firstFileCrawlFromPercent = Math.max(
					currentPercent,
					ZIP_PROGRESS_FLOOR_PERCENT
				)
				phase = 'firstFileCrawl'
			}

			if (nextFilesProcessed <= 0 || nextTotalFiles <= 0) {
				return
			}

			if (zippingStartedAt === null) {
				return
			}

			const elapsedMs = at - zippingStartedAt

			if (elapsedMs <= 0) {
				return
			}

			estimatedTotalMs = elapsedMs * (nextTotalFiles / nextFilesProcessed)
		},
		reset: (): void => {
			estimatedTotalMs = null
			filesProcessed = 0
			firstFileCrawlFromPercent = ZIP_PROGRESS_FLOOR_PERCENT
			firstFileCrawlStartedAt = null
			highestPercentComplete = 0
			phase = 'idle'
			preparingStartedAt = null
			totalFiles = 0
			zippingStartedAt = null
		},
		setTotalFiles: (nextTotalFiles): void => {
			totalFiles = nextTotalFiles
		},
	}
}
